import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';
import { StudioFormHeader, StudioPage } from '../../components/StudioPage';

const EditUser = () => {
  const { t } = useTranslation();
  const params = useParams();
  const userId = params.id;
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    username: '',
    display_name: '',
    password: '',
    github_id: '',
    wechat_id: '',
    email: '',
    quota: 0,
    group: 'default',
    channel_ids: [],
    channel_names: [],
  });
  const [groupOptions, setGroupOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [channelNameOptions, setChannelNameOptions] = useState([]);
  const {
    username,
    display_name,
    password,
    github_id,
    wechat_id,
    email,
    quota,
  } = inputs;
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };
  const loadChannels = async () => {
    try {
      let res = await API.get(`/api/channel/?size=0`);
      const { success, data } = res.data || {};
      if (success && data) {
        setChannelOptions(
          data.map((ch) => {
            return { key: ch.id, text: `#${ch.id} ${ch.name}`, value: ch.id };
          })
        );
        // 同名渠道组：按渠道名去重
        const nameSet = new Set();
        const names = [];
        data.forEach((ch) => {
          if (ch.name && !nameSet.has(ch.name)) {
            nameSet.add(ch.name);
            names.push({ key: ch.name, text: ch.name, value: ch.name });
          }
        });
        setChannelNameOptions(names);
      }
    } catch (error) {
      // 非管理员调用会被后端拒绝，静默处理
    }
  };
  const navigate = useNavigate();
  const handleCancel = () => {
    navigate('/setting');
  };
  const loadUser = async () => {
    let res = undefined;
    if (userId) {
      res = await API.get(`/api/user/${userId}`);
    } else {
      res = await API.get(`/api/user/self`);
    }
    const { success, message, data } = res.data;
    if (success) {
      data.password = '';
      // 渠道ID/渠道名白名单后端存逗号分隔字符串，前端转数组
      data.channel_ids = data.channel_ids
        ? data.channel_ids.split(',').filter(Boolean).map(Number)
        : [];
      data.channel_names = data.channel_names
        ? data.channel_names.split(',').filter(Boolean)
        : [];
      setInputs(data);
    } else {
      showError(message);
    }
    setLoading(false);
  };
  useEffect(() => {
    loadUser().then();
    if (userId) {
      fetchGroups().then();
      loadChannels().then();
    }
  }, []);

  const submit = async () => {
    let res = undefined;
    if (userId) {
      let data = { ...inputs, id: parseInt(userId) };
      if (typeof data.quota === 'string') {
        data.quota = parseInt(data.quota);
      }
      // 渠道ID/渠道名白名单转逗号分隔字符串提交
      data.channel_ids = data.channel_ids.join(',');
      data.channel_names = data.channel_names.join(',');
      res = await API.put(`/api/user/`, data);
    } else {
      res = await API.put(`/api/user/self`, inputs);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('user.messages.update_success'));
    } else {
      showError(message);
    }
  };

  return (
    <StudioPage compact className='studio-user-form-page'>
      <div className='studio-surface studio-form-shell'>
        <div style={{ padding: 24 }}>
          <StudioFormHeader icon='user outline' title={t('user.edit.title')} />
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('user.edit.username')}
                name='username'
                placeholder={t('user.edit.username_placeholder')}
                onChange={handleInputChange}
                value={username}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.password')}
                name='password'
                type={'password'}
                placeholder={t('user.edit.password_placeholder')}
                onChange={handleInputChange}
                value={password}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.display_name')}
                name='display_name'
                placeholder={t('user.edit.display_name_placeholder')}
                onChange={handleInputChange}
                value={display_name}
                autoComplete='new-password'
              />
            </Form.Field>
            {userId && (
              <>
                <Form.Field>
                  <Form.Dropdown
                    label={t('user.edit.group')}
                    placeholder={t('user.edit.group_placeholder')}
                    name='group'
                    fluid
                    search
                    selection
                    allowAdditions
                    additionLabel={t('user.edit.group_addition')}
                    onChange={handleInputChange}
                    value={inputs.group}
                    autoComplete='new-password'
                    options={groupOptions}
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label={`${t('user.edit.quota')}${renderQuotaWithPrompt(
                      quota,
                      t
                    )}`}
                    name='quota'
                    placeholder={t('user.edit.quota_placeholder')}
                    onChange={handleInputChange}
                    value={quota}
                    type={'number'}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Dropdown
                    label={t('user.edit.channel_ids')}
                    placeholder={t('user.edit.channel_ids_placeholder')}
                    name='channel_ids'
                    fluid
                    multiple
                    clearable
                    search
                    selection
                    onChange={handleInputChange}
                    value={inputs.channel_ids}
                    options={channelOptions}
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Dropdown
                    label={t('user.edit.channel_names')}
                    placeholder={t('user.edit.channel_names_placeholder')}
                    name='channel_names'
                    fluid
                    multiple
                    clearable
                    search
                    selection
                    onChange={handleInputChange}
                    value={inputs.channel_names}
                    options={channelNameOptions}
                  />
                </Form.Field>
              </>
            )}
            <Form.Field>
              <Form.Input
                label={t('user.edit.github_id')}
                name='github_id'
                value={github_id}
                autoComplete='new-password'
                placeholder={t('user.edit.github_id_placeholder')}
                readOnly
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.wechat_id')}
                name='wechat_id'
                value={wechat_id}
                autoComplete='new-password'
                placeholder={t('user.edit.wechat_id_placeholder')}
                readOnly
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.email')}
                name='email'
                value={email}
                autoComplete='new-password'
                placeholder={t('user.edit.email_placeholder')}
                readOnly
              />
            </Form.Field>
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                marginTop: 12,
                paddingTop: 18,
                borderTop: '1px solid rgba(24, 24, 27, 0.08)',
              }}
            >
              <Button type='button' onClick={handleCancel}>
                {t('user.edit.buttons.cancel')}
              </Button>
              <Button type='button' positive onClick={submit}>
                {t('user.edit.buttons.submit')}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </StudioPage>
  );
};

export default EditUser;
